import React, {useState, useEffect} from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ButtonBase from '@mui/material/ButtonBase';
import { useTheme, alpha } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CarIcon from '@mui/icons-material/DirectionsCar';
import ExitIcon from '@mui/icons-material/ExitToApp';
import OfferIcon from '@mui/icons-material/LocalOffer';
import NotifyIcon from '@mui/icons-material/NotificationsActive';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MoneyIcon from '@mui/icons-material/Money';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {calcEst, showEst, optionsRequired} from '../common/sharedFunctions';
import SosIcon from '@mui/icons-material/Sos';
import LogoutIcon from '@mui/icons-material/Logout';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';



const drawerWidth = 248;

export default function ResponsiveDrawer(props) {
  const { t, i18n  } = useTranslation();
  const isRTL = i18n.dir();
  const theme = useTheme();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const {
    signOff
  } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();
  const location = useLocation();

  const LogOut = () => {
    dispatch(signOff());
  };

  const [role, setRole] = useState(null);

  useEffect(() => {
    if(auth.profile && auth.profile.usertype){
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawer = () => {
    setMobileOpen(false);
  };

  // Skill 6: AppBar avatar dropdown menu
  const [userMenuEl, setUserMenuEl] = useState(null);
  const userMenuOpen = Boolean(userMenuEl);
  const handleUserMenuOpen = (e) => setUserMenuEl(e.currentTarget);
  const handleUserMenuClose = () => setUserMenuEl(null);

  const menuItems = [
    {name : t('home'), url:'/', icon: <HomeIcon/>, access: ['admin','fleetadmin','driver','customer']},
    {name : t('dashboard_text'), url:'/dashboard', icon: <DashboardIcon/>, access: ['admin','fleetadmin']},
    {name : t('booking_history'), url:'/bookings', icon: <ViewListIcon/>, access: ['admin','fleetadmin','driver','customer']},
    {name : t('addbookinglable'), url:'/addbookings', icon: <ContactPhoneIcon/>, access: calcEst && !showEst && !optionsRequired ? ['customer'] : ['admin','fleetadmin','customer']},
    {name : t('user'), url:'/users', icon: <EmojiPeopleIcon />, access: ['admin','fleetadmin']},
    {name : t('car_type'), url:'/cartypes', icon: <CarIcon/>, access: ['admin']},
    {name : t('cars'), url:'/cars', icon: <CarIcon/>, access: ['admin','fleetadmin','driver']},
    {name : t('withdraws_web'), url:'/withdraws', icon: <MoneyIcon />, access: ['admin']},
    {name : t('add_to_wallet'), url:'/addtowallet', icon: <AccountBalanceWalletIcon />, access: ['admin']},
    {name : t('report'), url:'/allreports', icon: <AssessmentIcon />, access: ['admin','fleetadmin']},
    {name : t('promo'), url:'/promos', icon: <OfferIcon />, access: ['admin']},
    {name : t('push_notifications'), url:'/notifications', icon: <NotifyIcon />, access: ['admin']},
    {name : t('sos'), url:'/sos', icon: <SosIcon />, access: ['admin']},
    {name : t('complain'), url:'/complain', icon: <AnnouncementOutlinedIcon/>, access: ['admin']},
    {name : t('my_wallet_tile'), url:'/userwallet', icon: <AccountBalanceWalletIcon />, access: ['driver','customer']},
    {name : t('settings_title'), url:'/settings', icon: <PhoneIphoneIcon />, access: ['admin']},
    {name : t('profile'), url:'/profile', icon: <AccountCircleIcon />, access: ['admin','fleetadmin','driver','customer']},
    {name : t('logout'), url:'logout', icon: <ExitIcon />, access: ['admin','fleetadmin','driver','customer']},
  ];

  const isSelected = (url) => {
    if (!url || url === 'logout') return false;
    if (url === '/') return location.pathname === '/';
    return location.pathname === url || location.pathname.startsWith(url + '/');
  };

  const profileName = auth.profile ? `${auth.profile.firstName || ''} ${auth.profile.lastName || ''}`.trim() : '';
  const avatarLetter = profileName ? profileName.charAt(0).toUpperCase() : '?';

  const drawer = (
    <Box
      sx={{
        height: '100%',
        backgroundColor: theme.palette.background.paper,
        borderRight: isRTL === 'rtl' ? 0 : `1px solid ${theme.palette.divider}`,
        borderLeft: isRTL === 'rtl' ? `1px solid ${theme.palette.divider}` : 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '24px',
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <img
          style={{ width: 96, height: 96, objectFit: 'contain' }}
          src={require("../assets/img/logo192x192.png").default}
          alt="Logo"
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {role ?
        <List disablePadding>
          {menuItems.map((item, index) => {
            if (!item.access.includes(role)) return null;
            const selected = isSelected(item.url);
            return (
              <ListItem
                key={"key" + index}
                disableGutters
                disablePadding
                onClick={handleDrawer}
                sx={{ display: 'block', mb: 0.25 }}
              >
                <ListItemButton
                  component={item.url === 'logout' ? 'div' : Link}
                  to={item.url === 'logout' ? undefined : item.url}
                  onClick={item.url === 'logout' ? LogOut : undefined}
                  selected={selected}
                  sx={{
                    height: 44,
                    mx: 1,
                    px: 1.5,
                    borderRadius: 2,
                    position: 'relative',
                    color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': {
                      backgroundColor: selected
                        ? alpha(theme.palette.primary.main, 0.12)
                        : 'rgba(224,47,57,0.04)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                    },
                    '&::before': selected ? {
                      content: '""',
                      position: 'absolute',
                      [isRTL === 'rtl' ? 'right' : 'left']: 0,
                      top: 8,
                      bottom: 8,
                      width: 3,
                      borderRadius: 2,
                      backgroundColor: theme.palette.primary.main,
                    } : undefined,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: selected ? 600 : 500,
                      textAlign: isRTL === 'rtl' ? 'right' : 'left',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        : null }
      </Box>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <CssBaseline />
      <style>
        {`
          ::-webkit-scrollbar { width: 8px; }
          ::-webkit-scrollbar-thumb {
            background-color: ${alpha(theme.palette.primary.main, 0.4)};
            border-radius: 6px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background-color: ${theme.palette.primary.main};
          }
          ::-webkit-scrollbar-track { background-color: ${theme.palette.background.default}; }
        `}
      </style>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: isRTL === 'rtl' ? 0 : { sm: `${drawerWidth}px` },
          mr: isRTL === 'rtl' ? { sm: `${drawerWidth}px` } : 0,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            aria-label={t('open_drawer') || 'Menüyü aç'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: theme.palette.text.primary }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              flexGrow: 1,
            }}
          >
            {settings && settings.appName ? settings.appName : ''}
          </Typography>
          {auth.profile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={profileName || t('profile')}>
                <ButtonBase
                  onClick={handleUserMenuOpen}
                  aria-label={profileName || t('profile')}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  aria-controls={userMenuOpen ? 'user-menu' : undefined}
                  sx={{
                    borderRadius: '50%',
                    transition: 'transform 200ms ease',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      fontSize: 15,
                      fontWeight: 600,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                    src={auth.profile.profile_image || undefined}
                  >
                    {avatarLetter}
                  </Avatar>
                </ButtonBase>
              </Tooltip>
              <Menu
                id="user-menu"
                anchorEl={userMenuEl}
                open={userMenuOpen}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      minWidth: 220,
                      mt: 1,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: '0 8px 24px rgba(17,24,39,0.10), 0 2px 6px rgba(17,24,39,0.06)',
                      overflow: 'visible',
                    },
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                    {profileName || t('profile')}
                  </Typography>
                  {auth.profile.email && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {auth.profile.email}
                    </Typography>
                  )}
                </Box>
                <Divider />
                <MenuItem
                  component={Link}
                  to="/profile"
                  onClick={handleUserMenuClose}
                  sx={{ gap: 1.25, py: 1 }}
                >
                  <AccountCircleIcon fontSize="small" color="action" />
                  {t('profile')}
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => { handleUserMenuClose(); LogOut(); }}
                  sx={{ gap: 1.25, py: 1, color: 'error.main' }}
                >
                  <LogoutIcon fontSize="small" />
                  {t('logout')}
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label={t('navigation') || 'Ana gezinme'}
      >
        <Drawer
          id="mobile-drawer"
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
            },
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
            },
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {props.children}
      </Box>
    </Box>
  );
}
